import { z } from 'zod';
import { HOMEWORK_PHOTOS_MAX, HOMEWORK_PHOTO_MAX_BYTES } from '@repo/shared-types';

/** 'YYYY-MM' with a real month number — '2026-13' must not reach a query. */
export const monthKeySchema = z
  .string()
  .regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'Tháng phải có dạng YYYY-MM');

const NOTE_MAX = 4000;

// ── Note templates (THI-193) ──────────────────────────────────────────────

/** Items per template group. */
export const NOTE_TEMPLATE_GROUP_MAX = 12;
/** Items per note tier on the wire: the live group PLUS the orphans a note
 *  may still carry from a previous template (each capped at the group max),
 *  so a save can never be rejected for content the teacher did not add
 *  (review I7). Pinned by `progress.test.ts`. */
export const NOTE_ITEMS_WIRE_MAX = NOTE_TEMPLATE_GROUP_MAX * 2;

export const noteTemplateItemSchema = z.object({
  /** Missing on rows the editor just added; the server assigns nanoid(8). */
  id: z.string().min(1).max(16).optional(),
  name: z.string().max(40),
  hint: z.string().max(80).nullable().optional(),
});

/** PUT /api/subjects/:id/note-template — body is the template or `null`
 *  ("Bỏ sườn"). Nameless rows are dropped server-side, not rejected. */
export const noteTemplateSchema = z.object({
  presetKey: z.enum(['music', 'art', 'exam', 'custom']).nullable(),
  classItems: z.array(noteTemplateItemSchema).max(NOTE_TEMPLATE_GROUP_MAX),
  studentItems: z.array(noteTemplateItemSchema).max(NOTE_TEMPLATE_GROUP_MAX),
  homeworkItems: z.array(noteTemplateItemSchema).max(NOTE_TEMPLATE_GROUP_MAX),
});
export const putNoteTemplateSchema = noteTemplateSchema.nullable();
export type NoteTemplateInput = z.infer<typeof noteTemplateSchema>;

/** One sectioned entry as the form sends it: the item id plus the name the
 *  form showed (snapshotted into the note) and the text. Empty text rows
 *  are stripped server-side. */
export const noteItemInputSchema = z.object({
  itemId: z.string().min(1).max(16),
  name: z.string().min(1).max(40),
  content: z.string().max(NOTE_MAX),
});

/**
 * PUT /api/sessions/:id/notes — the whole form in one payload.
 *
 * Written longhand and BOTH keys optional on purpose (INVARIANTS §4): an
 * omitted key means "this tier was not part of this save", while a key
 * PRESENT with empty strings means "clear it", which deletes the row. The
 * form always sends both, so the omitted branch only protects a partial
 * caller from wiping a tier it never showed.
 */
// ── Homework photos (THI-196) ─────────────────────────────────────────────

/** One photo on the wire: what the client got back from presign plus the
 *  dimensions it measured after compressing. `key` is re-checked against the
 *  org/session prefix server-side; the schema only bounds its length. */
export const homeworkPhotoInputSchema = z.object({
  id: z.string().min(1).max(32),
  key: z.string().min(1).max(200),
  width: z.number().int().positive(),
  height: z.number().int().positive(),
  bytes: z.number().int().positive(),
});
export const homeworkPhotosSchema = z.array(homeworkPhotoInputSchema).max(HOMEWORK_PHOTOS_MAX);

/** POST /api/sessions/:id/homework-photos/presign. `pendingCount` = photos
 *  already in this group in the client's draft (saved or not, minus the one
 *  being presigned), so the presign refuses an eleventh before the upload.
 *  The real quota is `cleanPhotos` on save; two tabs can each hold ten. */
export const presignHomeworkPhotoSchema = z
  .object({
    tier: z.enum(['CLASS', 'STUDENT']),
    studentId: z.string().min(1).optional(),
    fileType: z.enum(['image/jpeg', 'image/png']),
    bytes: z.number().int().positive().max(HOMEWORK_PHOTO_MAX_BYTES),
    pendingCount: z.number().int().min(0).max(HOMEWORK_PHOTOS_MAX),
  })
  .refine((v) => v.tier !== 'STUDENT' || Boolean(v.studentId), { message: 'studentId is required for a STUDENT photo', path: ['studentId'] });
export type PresignHomeworkPhotoInput = z.infer<typeof presignHomeworkPhotoSchema>;

export const putSessionNotesSchema = z.object({
  classNote: z
    .object({
      comment: z.string().max(NOTE_MAX),
      homework: z.string().max(NOTE_MAX),
      items: z.array(noteItemInputSchema).max(NOTE_ITEMS_WIRE_MAX).optional(),
      homeworkItems: z.array(noteItemInputSchema).max(NOTE_ITEMS_WIRE_MAX).optional(),
      // Omitted = leave the stored photos alone; [] = clear; a list = replace.
      homeworkPhotos: homeworkPhotosSchema.optional(),
    })
    .optional(),
  studentNotes: z
    .array(
      z.object({
        studentId: z.string().min(1),
        comment: z.string().max(NOTE_MAX),
        homework: z.string().max(NOTE_MAX),
        items: z.array(noteItemInputSchema).max(NOTE_ITEMS_WIRE_MAX).optional(),
        homeworkItems: z.array(noteItemInputSchema).max(NOTE_ITEMS_WIRE_MAX).optional(),
        homeworkPhotos: homeworkPhotosSchema.optional(),
        // THI-346: omitted = leave the stored level alone; null = clear; 1..5 = set.
        rating: z.number().int().min(1).max(5).nullable().optional(),
      }),
    )
    .max(200)
    .optional(),
});
export type PutSessionNotesInput = z.infer<typeof putSessionNotesSchema>;

/**
 * PUT /api/students/:id/monthly-reviews/:subjectId/:month.
 * `aiDrafted` is what the CLIENT says about the content's origin: true when
 * the text came from a draft, even after editing. Absent means hand-written.
 */
export const upsertMonthlyReviewSchema = z.object({
  content: z.string().trim().min(1, 'Nhận xét không được để trống').max(NOTE_MAX),
  aiDrafted: z.boolean().default(false),
  // THI-348: absent = PUBLISHED, so every existing caller keeps publishing.
  // DRAFT is what the batch AI run saves; the route refuses a DRAFT over a
  // PUBLISHED review.
  status: z.enum(['DRAFT', 'PUBLISHED']).default('PUBLISHED'),
});
export type UpsertMonthlyReviewInput = z.infer<typeof upsertMonthlyReviewSchema>;

/**
 * POST /api/students/:id/progress-link. Rotating an EXISTING token needs the
 * explicit flag: a create-if-missing call must never silently invalidate a
 * link a parent already holds.
 */
export const progressLinkSchema = z.object({
  regenerate: z.boolean().default(false),
});
export type ProgressLinkInput = z.infer<typeof progressLinkSchema>;

/**
 * POST /api/students/progress-links/bulk. `studentIds` omitted means "every
 * ACTIVE student in the org"; present means "only these" (the invoice
 * review card scopes to the students it's showing). No `regenerate` field —
 * bulk creation never rotates an existing token (Global Constraints).
 */
export const bulkProgressLinkSchema = z.object({
  studentIds: z.array(z.string().min(1)).max(500).optional(),
});
export type BulkProgressLinkInput = z.infer<typeof bulkProgressLinkSchema>;
